import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Header, Footer } from '../../components';
import storage from '../../utils/storage';
import MarkdownIt from 'markdown-it';
import hljs from 'highlight.js';
import './WritePost.css';

// 初始化Markdown解析器
const md = new MarkdownIt({
  highlight: function (str, lang) {
    if (lang && hljs.getLanguage(lang)) {
      try {
        return '<pre class="hljs"><code>' +
               hljs.highlight(str, { language: lang }).value +
               '</code></pre>';
      } catch (__) {}
    }
    return '<pre class="hljs"><code>' + md.utils.escapeHtml(str) + '</code></pre>';
  }
});

const WritePost = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentUser, setCurrentUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editPostId, setEditPostId] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    tags: ''
  });
  
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    const user = storage.getCurrentUser();
    if (!user) {
      alert('请先登录后再发布文章');
      navigate('/auth');
      return;
    }
    
    setCurrentUser(user);
    
    // 检查是否是编辑模式
    const editId = searchParams.get('edit');
    if (editId) {
      loadPostForEdit(editId);
    }
  }, [navigate, searchParams]);

  const loadPostForEdit = (postId) => {
    try {
      const posts = storage.getPosts();
      const post = posts.find(p => p.id === postId);
      
      if (post) {
        setIsEditing(true);
        setEditPostId(postId);
        setFormData({
          title: post.title,
          content: post.content,
          tags: post.tags ? post.tags.join(', ') : ''
        });
      } else {
        alert('文章不存在');
        navigate('/');
      }
    } catch (error) {
      console.error('Error loading post for edit:', error);
      alert('加载文章失败');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // 清除错误
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = '文章标题不能为空';
    } else if (formData.title.length < 3) {
      newErrors.title = '标题至少需要3个字符';
    } else if (formData.title.length > 100) {
      newErrors.title = '标题不能超过100个字符';
    }

    if (!formData.content.trim()) {
      newErrors.content = '文章内容不能为空';
    } else if (formData.content.length < 10) {
      newErrors.content = '文章内容至少需要10个字符';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!currentUser) {
      alert('请先登录');
      navigate('/auth');
      return;
    }

    setSaving(true);

    try {
      const posts = storage.getPosts();
      const tags = formData.tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);

      if (isEditing) {
        // 更新现有文章
        const postIndex = posts.findIndex(p => p.id === editPostId);
        if (postIndex !== -1) {
          posts[postIndex] = {
            ...posts[postIndex],
            title: formData.title.trim(),
            content: formData.content.trim(),
            tags: tags,
            updatedAt: new Date().toISOString()
          };
          
          storage.savePosts(posts);
          alert('文章更新成功！');
          navigate(`/post/${editPostId}`);
        }
      } else {
        // 创建新文章
        const newPost = {
          id: `post_${Date.now()}`,
          title: formData.title.trim(),
          content: formData.content.trim(),
          author: currentUser.id,
          tags: tags,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          views: 0,
          likes: 0
        };
        
        posts.unshift(newPost);
        storage.savePosts(posts);
        alert('文章发布成功！');
        navigate(`/post/${newPost.id}`);
      }
    } catch (error) {
      console.error('Error saving post:', error);
      alert('保存失败，请稍后重试');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = () => {
    try {
      const draft = {
        title: formData.title,
        content: formData.content,
        tags: formData.tags,
        savedAt: new Date().toISOString()
      };
      
      localStorage.setItem('blog_draft', JSON.stringify(draft));
      alert('草稿已保存');
    } catch (error) {
      console.error('Error saving draft:', error);
      alert('保存草稿失败');
    }
  };

  const loadDraft = () => {
    try {
      const draft = localStorage.getItem('blog_draft');
      if (draft) {
        const draftData = JSON.parse(draft);
        setFormData({
          title: draftData.title || '',
          content: draftData.content || '',
          tags: draftData.tags || ''
        });
        alert('草稿已加载');
      } else {
        alert('没有找到草稿');
      }
    } catch (error) {
      console.error('Error loading draft:', error);
      alert('加载草稿失败');
    }
  };

  const renderPreview = () => {
    return {
      __html: md.render(formData.content)
    };
  };

  const insertMarkdown = (prefix, suffix = '') => {
    const textarea = document.getElementById('content');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = formData.content.substring(start, end);
    
    const replacement = prefix + selectedText + suffix;
    const newContent = 
      formData.content.substring(0, start) + 
      replacement + 
      formData.content.substring(end);
    
    setFormData(prev => ({
      ...prev,
      content: newContent
    }));
    
    // 重新聚焦并设置光标位置
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + prefix.length + selectedText.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  return (
    <div className="app">
      <Header />
      <main className="main">
        <div className="container-sm">
          <div className="write-container">
            <div className="write-header">
              <h1>{isEditing ? '编辑文章' : '撰写新文章'}</h1>
              <div className="write-actions">
                <button 
                  onClick={() => setPreviewMode(!previewMode)}
                  className={`btn ${previewMode ? 'btn-secondary' : 'btn-primary'}`}
                >
                  {previewMode ? '编辑' : '预览'}
                </button>
                <button 
                  onClick={handleSaveDraft}
                  className="btn btn-secondary"
                >
                  保存草稿
                </button>
                <button 
                  onClick={loadDraft}
                  className="btn btn-secondary"
                >
                  加载草稿
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="write-form">
              <div className="form-section">
                <label htmlFor="title">文章标题 *</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="请输入文章标题"
                  maxLength="100"
                />
                {errors.title && (
                  <span className="error-text">{errors.title}</span>
                )}
              </div>

              <div className="form-section">
                <label htmlFor="tags">文章标签</label>
                <input
                  type="text"
                  id="tags"
                  name="tags"
                  value={formData.tags}
                  onChange={handleInputChange}
                  placeholder="请输入标签，用逗号分隔"
                />
                <small className="form-help">
                  用逗号分隔多个标签，例如：React, JavaScript, 教程
                </small>
              </div>

              <div className="form-section">
                <div className="editor-toolbar">
                  <label htmlFor="content">文章内容 *</label>
                  <div className="toolbar-buttons">
                    <button 
                      type="button"
                      onClick={() => insertMarkdown('**', '**')}
                      title="加粗"
                    >
                      <strong>B</strong>
                    </button>
                    <button 
                      type="button"
                      onClick={() => insertMarkdown('*', '*')}
                      title="斜体"
                    >
                      <em>I</em>
                    </button>
                    <button 
                      type="button"
                      onClick={() => insertMarkdown('# ')}
                      title="标题"
                    >
                      H1
                    </button>
                    <button 
                      type="button"
                      onClick={() => insertMarkdown('[', '](url)')}
                      title="链接"
                    >
                      🔗
                    </button>
                    <button 
                      type="button"
                      onClick={() => insertMarkdown('```\n', '\n```')}
                      title="代码块"
                    >
                      {'<>'}
                    </button>
                    <button 
                      type="button"
                      onClick={() => insertMarkdown('- ')}
                      title="列表"
                    >
                      📝
                    </button>
                  </div>
                </div>

                {previewMode ? (
                  <div 
                    className="preview-content"
                    dangerouslySetInnerHTML={renderPreview()}
                  />
                ) : (
                  <textarea
                    id="content"
                    name="content"
                    value={formData.content}
                    onChange={handleInputChange}
                    placeholder="请输入文章内容，支持Markdown语法..."
                    rows="20"
                  />
                )}
                {errors.content && (
                  <span className="error-text">{errors.content}</span>
                )}
              </div>

              <div className="form-actions">
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={saving}
                >
                  {saving ? '保存中...' : (isEditing ? '更新文章' : '发布文章')}
                </button>
                <button 
                  type="button" 
                  onClick={() => navigate(-1)}
                  className="btn btn-secondary"
                >
                  取消
                </button>
              </div>
            </form>

            {/* Markdown语法帮助 */}
            <div className="markdown-help">
              <h3>Markdown语法帮助</h3>
              <div className="help-grid">
                <div className="help-item">
                  <code># 标题</code>
                  <span>一级标题</span>
                </div>
                <div className="help-item">
                  <code>**粗体**</code>
                  <span>粗体文本</span>
                </div>
                <div className="help-item">
                  <code>*斜体*</code>
                  <span>斜体文本</span>
                </div>
                <div className="help-item">
                  <code>```代码```</code>
                  <span>代码块</span>
                </div>
                <div className="help-item">
                  <code>[链接](url)</code>
                  <span>超链接</span>
                </div>
                <div className="help-item">
                  <code>![图片](url)</code>
                  <span>图片</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default WritePost;